import { useState, useEffect } from 'react';
import slugify from 'slugify';
import { uploadImageFile } from '../../../utils/api';

const validateContentLinksAndImages = (html) => {
  if (!html) return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Check links: allow http, https, mailto, tel, relative paths (/...), or in-page anchors (#)
  const invalidLink = Array.from(doc.querySelectorAll('a')).some((link) => {
    const href = link.getAttribute('href')?.trim();
    // If empty href, anchor, or empty action, do not block user
    if (!href || href === '#' || !href.includes('.')) {
      return false;
    }
    const isValid =
      /^https?:\/\//i.test(href) ||
      /^mailto:/i.test(href) ||
      /^tel:/i.test(href) ||
      /^\//i.test(href) ||
      /^#/i.test(href);

    return !isValid;
  });

  if (invalidLink) {
    return 'One or more links appear invalid. Use valid website URLs (https://...), email (mailto:), phone (tel:), or internal paths (e.g. /contact).';
  }

  const invalidImage = Array.from(doc.querySelectorAll('img')).some((img) => {
    const src = img.getAttribute('src')?.trim() || '';
    return !src;
  });

  if (invalidImage) {
    return 'One or more images have missing source URLs.';
  }

  return null;
};

export const useBlogForm = ({ onSubmit, initialData = {} }) => {
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    slug: initialData.slug || '',
    metaTitle: initialData.metaTitle || '',
    metaDescription: initialData.metaDescription || '',
    shortDescription: initialData.shortDescription || '',
    content: initialData.content || '',
    featuredImage: initialData.featuredImage || '',
    authorName: initialData.authorName || '',
    category: initialData.category || '',
    tags: initialData.tags || [],
    status: initialData.status || 'Draft',
    readingTime: initialData.readingTime || 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData.title && !initialData.slug) {
      setFormData((prev) => ({
        ...prev,
        slug: slugify(initialData.title, { lower: true, strict: true }),
      }));
    }
  }, [initialData.title, initialData.slug]);

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title: newTitle,
      slug: slugify(newTitle, { lower: true, strict: true }),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field-level error when user types and corrects it
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        delete copy.form;
        return copy;
      });
    }
  };

  const handleContentChange = (value) => {
    // Estimate reading time (~200 words per minute)
    const text = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const wordCount = text ? text.split(/\s+/).length : 0;
    const estimatedMinutes = Math.max(1, Math.ceil(wordCount / 200));

    setFormData((prev) => ({
      ...prev,
      content: value,
      readingTime: prev.readingTime && prev.readingTime > 0 && initialData?._id ? prev.readingTime : estimatedMinutes,
    }));

    if (errors.content) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy.content;
        delete copy.form;
        return copy;
      });
    }
  };

  const handleTagsChange = (newTags) => {
    setFormData((prev) => ({ ...prev, tags: newTags }));
    if (errors.tags) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy.tags;
        delete copy.form;
        return copy;
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          featuredImage: 'Image size cannot exceed 15MB.',
        }));
        e.target.value = '';
        return;
      }

      setImageFile(file);
      setFormData((prev) => ({ ...prev, featuredImage: URL.createObjectURL(file) }));
      if (errors.featuredImage) {
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy.featuredImage;
          delete copy.form;
          return copy;
        });
      }
      return;
    }

    // Allow manual removal of existing image when no file is selected.
    setImageFile(null);
    setFormData((prev) => ({ ...prev, featuredImage: '' }));
  };
  
  const validate = (dataToValidate = formData) => {
    const newErrors = {};

    // 1. Title Validation (Required & Max 120 chars)
    if (!dataToValidate.title?.trim()) {
      newErrors.title = 'Title is required';
    } else if (dataToValidate.title.length > 120) {
      newErrors.title = `Title cannot exceed 120 characters (currently ${dataToValidate.title.length} chars).`;
    }

    // 2. Slug Validation
    if (!dataToValidate.slug?.trim()) {
      newErrors.slug = 'Slug is required';
    }

    // 3. Meta Title Validation (Required & Strict Max 60 chars)
    if (!dataToValidate.metaTitle?.trim()) {
      newErrors.metaTitle = 'Meta title is required';
    } else if (dataToValidate.metaTitle.length > 60) {
      newErrors.metaTitle = `Meta title cannot exceed 60 characters (currently ${dataToValidate.metaTitle.length}/60 chars). Please shorten it by ${dataToValidate.metaTitle.length - 60} character(s).`;
    }

    // 4. Meta Description Validation (Required & Strict Max 160 chars)
    if (!dataToValidate.metaDescription?.trim()) {
      newErrors.metaDescription = 'Meta description is required';
    } else if (dataToValidate.metaDescription.length > 160) {
      newErrors.metaDescription = `Meta description cannot exceed 160 characters (currently ${dataToValidate.metaDescription.length}/160 chars). Please shorten it by ${dataToValidate.metaDescription.length - 160} character(s).`;
    }

    // 5. Short Description Validation (Required & Strict Max 250 chars)
    if (!dataToValidate.shortDescription?.trim()) {
      newErrors.shortDescription = 'Short description is required';
    } else if (dataToValidate.shortDescription.length > 250) {
      newErrors.shortDescription = `Short description cannot exceed 250 characters (currently ${dataToValidate.shortDescription.length}/250 chars). Please shorten it by ${dataToValidate.shortDescription.length - 250} character(s).`;
    }

    // 6. Article Body Validation
    const plainTextContent = (dataToValidate.content || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!plainTextContent) {
      newErrors.content = 'Content is required';
    } else if (plainTextContent.length < 20) {
      newErrors.content = 'Content must be at least 20 characters long.';
    }

    const richContentError = validateContentLinksAndImages(dataToValidate.content);
    if (richContentError) {
      newErrors.content = richContentError;
    }

    // 7. Taxonomy & Media Validation
    if (!dataToValidate.featuredImage) {
      newErrors.featuredImage = 'Featured image is required';
    }
    if (!dataToValidate.authorName?.trim()) {
      newErrors.authorName = 'Author name is required';
    }
    if (!dataToValidate.category?.trim()) {
      newErrors.category = 'Category is required';
    }
    if (!dataToValidate.tags || dataToValidate.tags.length === 0) {
      newErrors.tags = 'At least one tag is required';
    }
    if (!dataToValidate.readingTime || dataToValidate.readingTime <= 0) {
      newErrors.readingTime = 'Reading time must be a positive number';
    }
    
    if (Object.keys(newErrors).length > 0) {
      newErrors.form = 'Cannot save: Character limit or required field validation failed. Please fix the highlighted errors below.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, overrides = {}) => {
    if (e && e.preventDefault) e.preventDefault();

    const dataToSubmit = {
      ...formData,
      ...overrides,
    };

    if (!validate(dataToSubmit)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    setIsSubmitting(true);

    let uploadedImageUrl = dataToSubmit.featuredImage;
    if (imageFile) {
      try {
        uploadedImageUrl = await uploadImageFile(imageFile);
      } catch (err) {
        console.error(err);
        setErrors({ form: 'Image upload failed. Please try again.' });
        setIsSubmitting(false);
        return false;
      }
    }

    const blogData = {
      ...initialData,
      ...dataToSubmit,
      featuredImage: uploadedImageUrl,
    };

    try {
      await onSubmit(blogData);
      return true;
    } catch (err) {
      setErrors({ form: err.message || 'Failed to save blog' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    handleInputChange,
    handleTitleChange,
    handleContentChange,
    handleTagsChange,
    handleImageChange,
    handleSubmit,
    validate,
    isSubmitting,
    errors,
  };
};